/**
 * Email validation utilities
 * 
 * Uses a comprehensive regex pattern that supports:
 * - Plus signs (+) in local part (e.g., user+tag@example.com)
 * - Multiple dots in local part (e.g., first.middle.last@example.com)
 * - Hyphens in domain (e.g., user@my-domain.com)
 * - Subdomains (e.g., user@mail.example.com)
 * - Various TLDs including newer long TLDs
 * - International characters in local part (basic support)
 * 
 * Note: This is a practical validation for most use cases. For complete
 * RFC 5322 compliance or international domain name (IDN) support,
 * consider using a specialized library.
 */

/**
 * Comprehensive email validation regex
 * 
 * Pattern breakdown:
 * - Local part: alphanumeric, dots, hyphens, underscores, plus signs
 * - @ symbol
 * - Domain: alphanumeric, dots, hyphens
 * - TLD: 2+ characters
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

/**
 * Validates an email address
 * @param email - The email address to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string | null | undefined): boolean {
    if (!email) return false
    const trimmed = email.trim()
    if (trimmed.length === 0) return false
    if (trimmed.length > 254) return false // RFC 5321 max length
    
    return EMAIL_REGEX.test(trimmed)
}

/**
 * Validates an email address and returns an error message if invalid
 * @param email - The email address to validate
 * @returns Error message if invalid, undefined if valid
 */
export function validateEmail(email: string | null | undefined): string | undefined {
    if (!email || email.trim().length === 0) {
        return undefined // Allow empty (use required validation separately)
    }
    
    const trimmed = email.trim()
    
    if (trimmed.length > 254) {
        return 'Email address is too long (max 254 characters).'
    }
    
    if (!EMAIL_REGEX.test(trimmed)) {
        return 'Email format is invalid.'
    }
    
    return undefined
}

/**
 * React Hook Form compatible email validator
 * @param required - Whether the email is required
 * @returns Validation rules object for React Hook Form
 */
export function emailValidationRules(required: boolean = false) {
    return {
        required: required ? 'Email is required.' : false,
        maxLength: { value: 254, message: 'Email address is too long (max 254 characters).' },
        validate: (value: string) => {
            if (!value || value.trim().length === 0) {
                return required ? 'Email is required.' : true
            }
            return isValidEmail(value) || 'Email format is invalid.'
        }
    }
}

/**
 * Phone number validation utilities
 * 
 * Supports various international phone number formats:
 * - Optional country code with + prefix (e.g., +1, +44, +91)
 * - Optional area/region code in parentheses or not (e.g., (555), 555)
 * - Various separators: spaces, hyphens, dots
 * - Minimum 6 digits (shortest valid international numbers)
 * - Maximum 25 characters total (E.164 is max 15 digits + formatting)
 * 
 * Valid examples:
 * - +1 (555) 123-4567
 * - +44 20 7123 4567
 * - 555-123-4567
 * - (555) 123.4567
 * - +91 98765 43210
 * - 1234567890
 */

/**
 * Phone number validation regex
 * 
 * Pattern breakdown:
 * - Optional country code: +1-3 digits followed by optional separator
 * - Optional area code: 2-4 digits, optionally in parentheses, followed by optional separator
 * - Main number: 3-4 digits followed by separator then 3-4 more digits
 * - Separators can be: space, hyphen, or dot
 */
const PHONE_REGEX = /^(\+?\d{1,3}[-.\s]?)?(\(?\d{2,4}\)?[-.\s]?)?\d{3,4}[-.\s]?\d{3,4}$/

/**
 * Validates a phone number
 * @param phone - The phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhone(phone: string | null | undefined): boolean {
    if (!phone) return false
    const trimmed = phone.trim()
    if (trimmed.length === 0) return false
    if (trimmed.length > 25) return false
    
    // Must contain at least 6 digits
    const digitCount = trimmed.replace(/\D/g, '').length
    if (digitCount < 6) return false
    
    return PHONE_REGEX.test(trimmed)
}

/**
 * Validates a phone number and returns an error message if invalid
 * @param phone - The phone number to validate
 * @returns Error message if invalid, undefined if valid
 */
export function validatePhone(phone: string | null | undefined): string | undefined {
    if (!phone || phone.trim().length === 0) {
        return undefined // Allow empty (use required validation separately)
    }
    
    const trimmed = phone.trim()
    
    if (trimmed.length > 25) {
        return 'Phone number is too long (max 25 characters).'
    }
    
    const digitCount = trimmed.replace(/\D/g, '').length
    if (digitCount < 6) {
        return 'Phone must contain at least 6 digits.'
    }
    
    if (!PHONE_REGEX.test(trimmed)) {
        return 'Phone format is invalid.'
    }
    
    return undefined
}

/**
 * React Hook Form compatible phone validator
 * @param required - Whether the phone is required
 * @returns Validation rules object for React Hook Form
 */
export function phoneValidationRules(required: boolean = false) {
    return {
        required: required ? 'Phone is required.' : false,
        validate: (value: string) => {
            if (!value || value.trim().length === 0) {
                return required ? 'Phone is required.' : true
            }
            const error = validatePhone(value)
            return error ? error : true
        }
    }
}
